'use client';
/**
 * Паттерны — второй слой системы: собраны из примитивов, знают про
 * плотность и состояния, но всё ещё не знают про бизнес-логику.
 * Именно здесь задаётся «характер» интерфейса: компактно, без карточек
 * внутри карточек, статус читается формой и цветом одновременно.
 */
import type { ReactNode } from 'react';
import { AlertTriangle, Inbox, TrendingUp, TrendingDown } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { cx, Button, Badge, type Tone } from '@/components/ui';

/* ── Шапка экрана ───────────────────────────────────────────────── */

export function PageHeader({ title, subtitle, actions, tabs }: {
  title: ReactNode; subtitle?: ReactNode; actions?: ReactNode; tabs?: ReactNode;
}) {
  return (
    <header className="flex flex-wrap items-start gap-3 mb-1">
      <div className="min-w-0">
        <h1 className="text-[24px] font-semibold tracking-[-0.02em] text-[var(--text-primary)] leading-tight">{title}</h1>
        {subtitle && <p className="mt-1 text-[13.5px] text-[var(--text-muted)]">{subtitle}</p>}
      </div>
      {actions && <div className="ml-auto flex items-center gap-2">{actions}</div>}
      {tabs && <div className="w-full">{tabs}</div>}
    </header>
  );
}

/* ── KPI ────────────────────────────────────────────────────────────
   Плитка максимально скучная: подпись, число, дельта, спарклайн.
   Никаких иконок в кружках и градиентов — цифра должна читаться первой. */

export interface KpiProps {
  label: string;
  value: ReactNode;
  unit?: string;
  delta?: number | null;
  /** Рост — это хорошо? У расхода топлива наоборот. */
  deltaGoodWhen?: 'up' | 'down';
  hint?: string;
  series?: number[];
  href?: string;
}

export function KpiCard({ label, value, unit, delta, deltaGoodWhen = 'up', hint, series }: KpiProps) {
  const good = delta == null ? null : deltaGoodWhen === 'up' ? delta >= 0 : delta <= 0;
  const Arrow = (delta ?? 0) >= 0 ? TrendingUp : TrendingDown;
  return (
    <div className="flex flex-col gap-2 px-4 py-[18px] bg-[var(--surface)] border border-[var(--border)] rounded-[var(--radius-md)]">
      <span className="text-[11.5px] font-medium uppercase tracking-[0.07em] text-[var(--text-muted)]">{label}</span>
      <div className="flex items-baseline gap-1.5">
        <b className="text-[28px] leading-none font-semibold tracking-[-0.025em] text-[var(--text-primary)] tabular-nums">{value}</b>
        {unit && <span className="text-[13px] text-[var(--text-muted)]">{unit}</span>}
        {delta != null && (
          <span className={cx(
            'ml-auto inline-flex items-center gap-0.5 text-[12px] font-medium tabular-nums',
            good ? 'text-[var(--success)]' : 'text-[var(--danger)]',
          )}>
            <Arrow size={13} />{delta > 0 ? '+' : ''}{delta}%
          </span>
        )}
      </div>
      {series && series.length > 1 && <Sparkline data={series} tone={good === false ? 'danger' : 'success'} />}
      {hint && <span className="text-[12.5px] text-[var(--text-muted)]">{hint}</span>}
    </div>
  );
}

export function KpiRow({ children }: { children: ReactNode }) {
  return <div className="grid gap-3 grid-cols-2 md:grid-cols-3 xl:grid-cols-6">{children}</div>;
}

/** Спарклайн с заливкой и подсвеченной последней точкой. */
export function Sparkline({ data, tone = 'success', height = 28 }: {
  data: number[]; tone?: 'success' | 'danger' | 'info'; height?: number;
}) {
  const w = 120;
  const min = Math.min(...data), max = Math.max(...data);
  const span = max - min || 1;
  const pts = data.map((v, i) => [
    (i / (data.length - 1)) * w,
    height - 3 - ((v - min) / span) * (height - 6),
  ] as const);
  const line = pts.map(([x, y], i) => `${i ? 'L' : 'M'}${x.toFixed(1)},${y.toFixed(1)}`).join(' ');
  const area = `${line} L${w},${height} L0,${height} Z`;
  const color = tone === 'danger' ? 'var(--danger)' : tone === 'info' ? 'var(--info)' : 'var(--success)';
  const [lx, ly] = pts[pts.length - 1];
  return (
    <svg viewBox={`0 0 ${w} ${height}`} width="100%" height={height} preserveAspectRatio="none" aria-hidden className="block">
      <path d={area} fill={color} opacity="0.10" />
      <path d={line} fill="none" stroke={color} strokeWidth="1.5" strokeLinejoin="round" vectorEffect="non-scaling-stroke" />
      <circle cx={lx} cy={ly} r="2" fill={color} />
    </svg>
  );
}

/* ── Состояния ──────────────────────────────────────────────────── */

export function EmptyState({ icon: Icon = Inbox, title, hint, action }: {
  icon?: LucideIcon; title: string; hint?: string; action?: ReactNode;
}) {
  return (
    <div className="flex flex-col items-center gap-2 py-12 px-6 text-center">
      <Icon size={20} className="text-[var(--text-muted)]" />
      <p className="text-[13px] font-medium text-[var(--text-primary)]">{title}</p>
      {hint && <p className="text-[12px] text-[var(--text-muted)] max-w-[42ch]">{hint}</p>}
      {action && <div className="mt-1">{action}</div>}
    </div>
  );
}

export function ErrorState({ message, onRetry }: { message: string; onRetry?: () => void }) {
  return (
    <div className="flex flex-col items-center gap-2.5 py-12 px-6 text-center">
      <AlertTriangle size={20} className="text-[var(--danger)]" />
      <p className="text-[13px] font-medium text-[var(--text-primary)]">{message}</p>
      {onRetry && <Button size="sm" onClick={onRetry}>Try again</Button>}
    </div>
  );
}

/* ── Список алертов ─────────────────────────────────────────────── */

export interface AlertItem {
  id: string;
  tone: Tone;
  title: string;
  meta?: string;
  href?: string;
}

export function AlertList({ items, empty }: { items: AlertItem[]; empty?: string }) {
  if (!items.length) {
    return <p className="px-4 py-7 text-center text-[13px] text-[var(--text-muted)]">{empty ?? 'Nothing needs attention'}</p>;
  }
  return (
    <ul className="divide-y divide-[var(--border)]">
      {items.map((a) => (
        <li key={a.id}>
          <a
            href={a.href ?? '#'}
            className="flex items-center gap-2.5 px-4 h-11 hover:bg-[var(--surface-hover)] transition-colors focus-visible:outline-none focus-visible:shadow-[var(--focus-ring)]"
          >
            <i className={cx('w-1.5 h-1.5 rounded-full shrink-0',
              a.tone === 'danger' ? 'bg-[var(--danger)]' : a.tone === 'warning' ? 'bg-[var(--warning)]' : 'bg-[var(--info)]')} />
            <span className="text-[13.5px] text-[var(--text-secondary)] truncate">{a.title}</span>
            {a.meta && <span className="ml-auto text-[13px] font-medium text-[var(--text-primary)] tabular-nums whitespace-nowrap">{a.meta}</span>}
          </a>
        </li>
      ))}
    </ul>
  );
}

/* ── Строка сущности: список заказов, водителей, техники ────────── */

export function EntityRow({ code, title, meta, badge, href }: {
  code?: string; title: ReactNode; meta?: ReactNode; badge?: ReactNode; href?: string;
}) {
  const inner = (
    <>
      <div className="min-w-0 flex-1">
        {code && <span className="block text-[11.5px] font-mono text-[var(--text-muted)] tracking-tight">{code}</span>}
        <span className="block text-[13.5px] text-[var(--text-primary)] truncate">{title}</span>
        {meta && <span className="block text-[12px] text-[var(--text-muted)] truncate">{meta}</span>}
      </div>
      {badge}
    </>
  );
  return (
    <li className="border-b border-[var(--border)] last:border-b-0">
      <a href={href ?? '#'} className="flex items-center gap-3 px-4 py-2.5 hover:bg-[var(--surface-hover)] transition-colors focus-visible:outline-none focus-visible:shadow-[var(--focus-ring)]">
        {inner}
      </a>
    </li>
  );
}

/** Пилюля-фильтр: «All 20 · New 6 · In transit 3». */
export function FilterPills<T extends string>({ value, onChange, options }: {
  value: T; onChange: (v: T) => void; options: { key: T; label: string; count?: number }[];
}) {
  return (
    <div className="flex flex-wrap items-center gap-1">
      {options.map((o) => {
        const active = o.key === value;
        return (
          <button
            key={o.key}
            onClick={() => onChange(o.key)}
            className={cx(
              'h-8 px-3 rounded-[var(--radius-sm)] border text-[12.5px] font-medium transition-colors',
              'focus-visible:outline-none focus-visible:shadow-[var(--focus-ring)]',
              active
                ? 'bg-[var(--brand-subtle)] border-[var(--brand-border)] text-[var(--brand)]'
                : 'bg-transparent border-[var(--border)] text-[var(--text-secondary)] hover:bg-[var(--surface-hover)]',
            )}
          >
            {o.label}
            {o.count != null && <span className="ml-1 tabular-nums opacity-70">{o.count}</span>}
          </button>
        );
      })}
    </div>
  );
}

export { Badge };
